import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { adminWarehouseApi } from 'api';

const WarehouseAddressEditor = ({
  warehouseId,
  initialBilling = null,
  initialShipping = null,
  refetch,
}) => {
  const [billing, setBilling] = useState({});
  const [shipping, setShipping] = useState({});
  const [loading, setLoading] = useState({ billing: false, shipping: false });
  const [error, setError] = useState(null);
  const [exists, setExists] = useState({ billing: false, shipping: false });

  // 초기 데이터 주입 (getById로 넘어온 값만 사용) + 존재 여부 판정
  useEffect(() => {
    // Billing
    if (initialBilling) {
      setBilling(initialBilling || {});
      setExists(prev => ({ ...prev, billing: true }));
    } else {
      setBilling({});
      setExists(prev => ({ ...prev, billing: false }));
    }

    // Shipping
    if (initialShipping) {
      setShipping(initialShipping || {});
      setExists(prev => ({ ...prev, shipping: true }));
    } else {
      setShipping({});
      setExists(prev => ({ ...prev, shipping: false }));
    }
  }, [initialBilling, initialShipping]);

  // 필드 값 변경 핸들러
  const handleChange = (e, type) => {
    const { name, value } = e.target;
    if (type === 'billing') {
      setBilling(prev => ({ ...prev, [name]: value }));
    } else {
      setShipping(prev => ({ ...prev, [name]: value }));
    }
  };

  // 존재 여부를 기준으로 POST(create) 또는 PATCH(update)
  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, [type]: true }));
    setError(null);

    const data = type === 'billing' ? billing : shipping;
    const isExisting = exists[type];

    try {
      if (isExisting) {
        await adminWarehouseApi.updateGatePassAddress(warehouseId, type, data);
        toast.success(
          `${
            type === 'billing' ? 'Billing' : 'Shipping'
          } address updated successfully`,
        );
      } else {
        await adminWarehouseApi.createGatePassAddress(warehouseId, type, data);
        toast.success(
          `${
            type === 'billing' ? 'Billing' : 'Shipping'
          } address created successfully`,
        );
        // 생성에 성공했으므로 존재 플래그 true로 전환
        setExists(prev => ({ ...prev, [type]: true }));
      }
      if (typeof refetch === 'function') refetch();
    } catch (err) {
      const action = isExisting ? 'update' : 'create';
      setError(err?.message || `Failed to ${action} address`);
      toast.error(
        `Failed to ${action} ${
          type === 'billing' ? 'billing' : 'shipping'
        } address`,
      );
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // 공통 폼 렌더러
  const renderForm = (type, label, state) => (
    <Card className="mt-4">
      <Card.Header className="bg-light">
        <h5 className="mb-0">{label}</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={e => handleSubmit(e, type)}>
          <Row>
            {[
              'name',
              'email',
              'phone',
              'address_line_1',
              'address_line_2',
              'city',
              'state',
              'pincode',
              'country',
            ].map(field => (
              <Col md={6} key={`${type}-${field}`} className="mb-3">
                <Form.Group controlId={`${type}-${field}`}>
                  <Form.Label className="text-capitalize">
                    {field.replaceAll('_', ' ')}
                  </Form.Label>
                  <Form.Control
                    name={field}
                    value={state[field] || ''}
                    onChange={e => handleChange(e, type)}
                    placeholder={`Enter ${field.replaceAll('_', ' ')}`}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
          {error && <div className="text-danger mb-2 small">{error}</div>}
          <div className="text-end mt-3">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={loading[type]}
            >
              {loading[type] ? 'Saving...' : `Save ${label}`}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );

  return (
    <div>
      {renderForm('billing', 'Billing Address', billing)}
      {renderForm('shipping', 'Shipping Address', shipping)}
    </div>
  );
};

export default WarehouseAddressEditor;
